import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Shield, X } from "lucide-react";
import {
  acceptAll,
  rejectAll,
  setConsent,
  getConsent,
  hasDecided,
} from "../lib/consent";

/**
 * GDPR-compliant cookie banner with 3-choice flow
 * + granular preferences modal.
 */
const CookieModal = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState({
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    if (!hasDecided()) {
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
    const existing = getConsent();
    if (existing) {
      setPrefs({
        analytics: existing.analytics,
        marketing: existing.marketing,
      });
    }
  }, []);

  // Allow opening preferences from footer link
  useEffect(() => {
    const handler = () => {
      setOpen(true);
      setPrefsOpen(true);
    };
    window.addEventListener("arabell-open-cookie-prefs", handler);
    return () => window.removeEventListener("arabell-open-cookie-prefs", handler);
  }, []);

  const close = () => {
    setOpen(false);
    setPrefsOpen(false);
  };

  const handleAcceptAll = () => {
    acceptAll();
    close();
  };

  const handleRejectAll = () => {
    rejectAll();
    close();
  };

  const handleSavePrefs = () => {
    setConsent({
      necessary: true,
      analytics: prefs.analytics,
      marketing: prefs.marketing,
    });
    close();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end md:items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-title"
      data-testid="cookie-banner"
    >
      <div className="max-w-2xl w-full bg-[#0a0a0a] border border-[#2a2722] rounded-sm overflow-hidden">
        {!prefsOpen ? (
          <div className="p-8 md:p-10 text-center">
            <div className="flex justify-center mb-4">
              <Shield className="w-10 h-10 text-arabell-gold" strokeWidth={1.2} />
            </div>
            <h3
              id="cookie-title"
              className="font-display text-2xl tracking-[0.18em] text-arabell-gold mb-3 uppercase"
            >
              {t("cookie.title")}
            </h3>
            <div className="w-12 h-px bg-arabell-gold mx-auto mb-5" />
            <p className="text-xs tracking-[0.25em] text-white/80 uppercase mb-5">
              {t("cookie.subtitle")}
            </p>
            <p className="text-sm text-white/70 leading-relaxed mb-4 text-left md:text-center">
              {t("cookie.body")}
            </p>
            <p className="text-xs text-white/50 leading-relaxed mb-6 text-left md:text-center">
              {t("cookie.policy_intro")}{" "}
              <Link
                to="/cookie-policy"
                className="underline text-white/80 hover:text-arabell-gold"
                onClick={close}
              >
                {t("cookie.cookie_policy")}
              </Link>{" "}
              {t("cookie.and")}{" "}
              <Link
                to="/privacy-policy"
                className="underline text-white/80 hover:text-arabell-gold"
                onClick={close}
              >
                {t("cookie.privacy_policy")}
              </Link>
              . {t("cookie.age_notice")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={handleRejectAll}
                data-testid="cookie-reject-all"
                className="w-full sm:w-auto px-6 py-3 text-[11px] tracking-[0.3em] uppercase border border-white/30 text-white/80 hover:border-white hover:text-white transition-colors duration-300"
              >
                {t("cookie.reject")}
              </button>
              <button
                onClick={() => setPrefsOpen(true)}
                data-testid="cookie-customize"
                className="w-full sm:w-auto px-6 py-3 text-[11px] tracking-[0.3em] uppercase border border-arabell-gold/40 text-arabell-gold hover:border-arabell-gold hover:bg-arabell-gold/10 transition-colors duration-300"
              >
                {t("cookie.customize")}
              </button>
              <button
                onClick={handleAcceptAll}
                data-testid="cookie-accept-all"
                className="w-full sm:w-auto px-6 py-3 text-[11px] tracking-[0.3em] uppercase bg-white text-black hover:bg-arabell-gold transition-colors duration-300"
              >
                {t("cookie.accept_all")}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-8 md:p-10">
            <div className="flex items-start justify-between mb-6">
              <h3 className="font-display text-xl tracking-[0.18em] text-arabell-gold uppercase">
                {t("cookie.prefs_title")}
              </h3>
              <button
                onClick={() => setPrefsOpen(false)}
                aria-label={t("cookie.close")}
                className="text-white/50 hover:text-white"
              >
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>

            <div className="space-y-4 mb-8 max-h-[50vh] overflow-y-auto pr-2">
              <CategoryRow
                title={t("cookie.necessary_title")}
                description={t("cookie.necessary_desc")}
                checked={true}
                disabled={true}
                testId="toggle-necessary"
              />
              <CategoryRow
                title={t("cookie.analytics_title")}
                description={t("cookie.analytics_desc")}
                checked={prefs.analytics}
                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                testId="toggle-analytics"
              />
              <CategoryRow
                title={t("cookie.marketing_title")}
                description={t("cookie.marketing_desc")}
                checked={prefs.marketing}
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                testId="toggle-marketing"
              />
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-end gap-3">
              <button
                onClick={handleRejectAll}
                className="w-full sm:w-auto px-6 py-3 text-[11px] tracking-[0.3em] uppercase border border-white/30 text-white/80 hover:border-white hover:text-white transition-colors duration-300"
              >
                {t("cookie.reject_all")}
              </button>
              <button
                onClick={handleSavePrefs}
                data-testid="cookie-save-prefs"
                className="w-full sm:w-auto px-6 py-3 text-[11px] tracking-[0.3em] uppercase bg-white text-black hover:bg-arabell-gold transition-colors duration-300"
              >
                {t("cookie.save")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CategoryRow = ({ title, description, checked, disabled, onChange, testId }) => (
  <div className="border border-white/10 bg-black/40 p-4 flex items-start gap-4">
    <button
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange && onChange(!checked)}
      data-testid={testId}
      className={`relative shrink-0 w-10 h-5 rounded-full transition-colors duration-200 ${
        checked ? "bg-arabell-gold" : "bg-white/20"
      } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`absolute top-0.5 h-4 w-4 rounded-full bg-black transition-transform duration-200 ${
          checked ? "translate-x-[22px]" : "translate-x-0.5"
        }`}
      />
    </button>
    <div className="flex-1">
      <p className="text-sm text-white font-medium mb-1">{title}</p>
      <p className="text-xs text-white/55 leading-relaxed">{description}</p>
    </div>
  </div>
);

export default CookieModal;
